import React from 'react';
import {
    TrendingUp, TrendingDown, Minus, Users, Activity, Send, UserPlus, Eye, Phone,
    MousePointer, DollarSign, Clock, Monitor, Share2, UserCheck, Heart, Percent,
} from 'lucide-react';

const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
    const h = Math.floor(m / 60);
    const remM = m % 60;
    return remM ? `${h}h ${remM}m` : `${h}h`;
};

const formatCount = (n) => (n == null ? '—' : Number(n).toLocaleString());
// Round then fix, the way the report grid did: plain toFixed(1) turns 82.55
// into "82.5" because of binary float, and the card would read a tenth low.
const formatPct = (n) => (n == null ? '—' : `${(Math.round(Number(n) * 10) / 10).toFixed(1)}%`);
const formatDecimal = (n) => (n == null ? '—' : Number(n).toFixed(1));

/**
 * Period-over-period chip. `comparison[key]` comes from the dashboard endpoint,
 * which measures the equally-long window immediately before the selected range
 * with the same code that measures the current one.
 *
 * `change_pct` is null off a zero base (no honest percentage exists there), so
 * the chip falls back to naming the previous value instead of inventing one.
 */
const DeltaChip = ({ change, format = formatCount }) => {
    if (!change) return null;
    const { previous, change_pct: changePct, direction } = change;
    const color = direction === 'up' ? 'text-emerald-600'
        : direction === 'down' ? 'text-orange-600'
        : 'text-gray-400';
    const Icon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
            <Icon className="w-3.5 h-3.5" />
            {changePct != null
                ? `${changePct > 0 ? '+' : ''}${changePct}%`
                : `was ${format(previous)}`}
        </span>
    );
};

/**
 * The one headline metric grid for the Report page.
 *
 * It used to be one of two: the Daily Report panel lower down the same page
 * carried its own "Key metrics" grid over the same measures, built from nightly
 * snapshots on its own week/month window, so the two grids disagreed on signups,
 * events, enquiries and session time. Christian asked for the key metrics in the
 * top overview with the duplicates gone (Monday 13037765152), so the three
 * measures only that grid had — all-time users, returning users and save rate —
 * moved here, the deltas came with them, and that grid was deleted.
 */
// `isOverall` is true when the page's date range is "Overall (all time)". There
// is no equally-long window before all-time, so the period-over-period delta
// chips degrade to a meaningless "was 0" — hide them entirely in that mode and
// show them only for the bounded ranges (Monday 13057918696).
const KPISummaryCards = ({ metrics, onCardClick, isOverall = false }) => {
    if (!metrics) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {Array.from({ length: 8 }).map((_, index) => (
                    <div
                        key={index}
                        className="bg-white rounded-lg border border-gray-200 p-5 animate-pulse"
                    >
                        <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
                        <div className="h-8 w-16 bg-gray-200 rounded" />
                    </div>
                ))}
            </div>
        );
    }

    const comparison = metrics.comparison || {};

    const cards = [
        // The old separate "Total Users (all-time)" tile was removed as a
        // duplicate — on the all-time range it read the same number as signups.
        // The signups tile keeps its own "New Sign Ups" label (id stays
        // `new_signups` so the drill-down handler keeps working). Active Buyers
        // sits next to it, ahead of Total Events (Monday 13057885503).
        {
            id: 'new_signups',
            title: 'New Sign Ups',
            value: formatCount(metrics.total_signups),
            icon: UserPlus,
            changeKey: 'total_signups',
            clickable: true,
            tooltip: 'Click to view signups',
            hasBreakdown: true
        },
        {
            id: 'unique_buyers',
            title: 'Active Buyers',
            value: formatCount(metrics.unique_buyers),
            icon: Users,
            changeKey: 'unique_buyers',
            clickable: true,
            tooltip: 'Signed-in buyers with at least one event in this range. Click to view buyer details.'
        },
        {
            id: 'total_events',
            title: 'Total Events',
            value: formatCount(metrics.total_events),
            icon: Activity,
            changeKey: 'total_events',
            clickable: true,
            tooltip: 'Click to view all events'
        },
        {
            id: 'enquiries',
            title: 'Enquiries Sent',
            value: formatCount(metrics.total_enquiries),
            icon: Send,
            changeKey: 'total_enquiries',
            clickable: true,
            tooltip: 'Click to view enquiries'
        },
        {
            id: 'conversion_rate',
            title: 'Conversion Rate',
            value: formatPct(metrics.conversion_rate),
            icon: Percent,
            changeKey: 'conversion_rate',
            changeFormat: formatPct,
            clickable: true,
            tooltip: 'Enquiries / Signups. Click to see which new signups enquired.'
        },
        {
            id: 'avg_session_duration',
            title: 'Avg Session Time',
            value: formatDuration(metrics.avg_session_duration_seconds),
            icon: Clock,
            changeKey: 'avg_session_duration_seconds',
            changeFormat: formatDuration,
            clickable: true,
            tooltip: `Avg ${metrics.avg_sessions_per_buyer || 0} sessions per buyer. Click to view session list.`
        },
        {
            id: 'returning_buyers',
            title: 'Returning Users',
            value: formatPct(metrics.returning_buyers_pct),
            icon: UserCheck,
            changeKey: 'returning_buyers_pct',
            changeFormat: formatPct,
            clickable: true,
            tooltip: `${formatCount(metrics.returning_buyers)} of the active buyers in this range were also active before it. Click to see who.`
        },
        {
            id: 'save_rate',
            title: 'Save Rate',
            value: formatPct(metrics.save_rate),
            icon: Heart,
            changeKey: 'save_rate',
            changeFormat: formatPct,
            clickable: true,
            tooltip: `${formatCount(metrics.property_savers)} of ${formatCount(metrics.property_viewers)} buyers who viewed a property saved one. Click to see who.`
        },
        {
            id: 'offers',
            title: 'Offers Received',
            value: formatCount(metrics.total_offers),
            icon: DollarSign,
            changeKey: 'total_offers',
            clickable: true,
            tooltip: 'Click to view offers'
        },
        {
            id: 'avg_events',
            title: 'Avg Events/Buyer',
            value: formatDecimal(metrics.avg_events_per_buyer),
            icon: Activity,
            changeKey: 'avg_events_per_buyer',
            changeFormat: formatDecimal,
            clickable: true,
            tooltip: 'Average events per buyer. Click to see each buyer\'s activity.'
        },
        {
            id: 'total_sessions',
            title: 'Total Sessions',
            value: formatCount(metrics.total_sessions),
            icon: Monitor,
            changeKey: 'total_sessions',
            clickable: true,
            tooltip: 'Distinct buyer-portal sessions in this window. Click to view session list.'
        },
        {
            id: 'gallery_views',
            title: 'Gallery Views',
            value: formatCount(metrics.total_gallery_views),
            icon: Eye,
            changeKey: 'total_gallery_views',
            clickable: true,
            tooltip: 'Click to view gallery interactions'
        },
        {
            id: 'intent_clicks',
            title: 'Intent Clicks (Phone/Email)',
            value: formatCount(
                metrics.total_intent_clicks
                ?? ((metrics.total_calls || 0) + (metrics.total_emails || 0))
            ),
            icon: Phone,
            changeKey: 'total_intent_clicks',
            clickable: true,
            tooltip: 'Click to view phone/email clicks'
        },
        {
            id: 'shares',
            title: 'Property Shares',
            value: formatCount(metrics.total_shares || 0),
            icon: Share2,
            changeKey: 'total_shares',
            clickable: true,
            tooltip: 'Click to see who shared which property'
        },
    ];

    const colorClasses = {
        gray: 'bg-gray-100 text-gray-400',
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {cards.map((card, index) => {
                const Icon = card.icon;
                const isClickable = card.clickable && onCardClick;
                const change = card.changeKey ? comparison[card.changeKey] : null;

                return (
                    <div
                        key={index}
                        onClick={() => isClickable && onCardClick(card.id, card.title)}
                        className={`bg-white rounded-lg border border-gray-200 p-5 relative group ${isClickable ? 'cursor-pointer hover:border-gray-300 transition-all duration-200' : ''
                            }`}
                        title={card.tooltip}
                    >
                        {isClickable && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <MousePointer className="w-4 h-4 text-blue-600" />
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                                <div className="flex items-baseline gap-2 flex-wrap mt-2">
                                    <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                                    {!isOverall && <DeltaChip change={change} format={card.changeFormat} />}
                                </div>
                                {isClickable && (
                                    <p className="text-xs text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        Click for details →
                                    </p>
                                )}
                                {card.hasBreakdown && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCardClick(`${card.id}_breakdown`, card.title);
                                        }}
                                        className="mt-3 text-[10px] font-bold uppercase tracking-wider text-purple-600 bg-purple-50 px-2 py-1 rounded hover:bg-purple-100 transition-colors inline-block"
                                    >
                                        View Breakdown
                                    </button>
                                )}
                            </div>
                            <div className={`p-2 rounded-lg ${colorClasses.gray}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default KPISummaryCards;
